import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPipelineRunDetails,
  getPipelineSteps,
  triggerPipelineStep,
} from "../api";
import { useAuth } from "../contexts/AuthContext";
import {
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  PauseCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { Breadcrumbs } from "../components/ui/breadcrumbs";
import PipelineStages from "../components/pipeline/PipelineStages";

const PipelineDetailsPage = () => {
  const { workspace, repoSlug, pipelineUuid } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { toast } = useToast();

  // State
  const [pipelineDetails, setPipelineDetails] = useState(null);
  const [pipelineSteps, setPipelineSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [error, setError] = useState(null);

  const mapStepsToStages = useCallback((stepsData) => {
    if (!stepsData || !stepsData.values) return [];

    return stepsData.values.map((step) => {
      // Calculate duration text
      let durationText = "";
      if (step.completed_on && step.started_on) {
        const duration =
          new Date(step.completed_on) - new Date(step.started_on);
        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);

        if (minutes > 0) {
          durationText = `${minutes}m ${seconds % 60}s`;
        } else {
          durationText = `${seconds}s`;
        }
      } else if (step.started_on) {
        durationText = "Running...";
      } else {
        durationText = "Waiting";
      }

      return {
        uuid: step.uuid,
        name: step.name || `Step ${step.sequence}`,
        status: step.state?.name || "PENDING",
        result: step.result?.name,
        duration: durationText,
        canBeManuallyStarted:
          step.state?.name === "PENDING" && step.can_be_manually_started,
      };
    });
  }, []);

  const fetchPipelineDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const [detailsData, stepsData] = await Promise.all([
        getPipelineRunDetails(workspace, repoSlug, pipelineUuid),
        getPipelineSteps(workspace, repoSlug, pipelineUuid),
      ]);

      setPipelineDetails(detailsData);

      // Process steps data into format needed for PipelineStages component
      const formattedSteps = mapStepsToStages(stepsData);
      setPipelineSteps(formattedSteps);

      setIsLoading(false);
    } catch (err) {
      setError("Failed to load pipeline details");
      setIsLoading(false);

      toast({
        title: "Error",
        description: "Failed to load pipeline details",
        variant: "destructive",
      });

      if (err.response && err.response.status === 401) {
        logout();
      }
    }
  }, [workspace, repoSlug, pipelineUuid, mapStepsToStages, toast, logout]);

  // Load pipeline details on mount and refresh while it is running.
  useEffect(() => {
    if (!workspace || !repoSlug || !pipelineUuid) {
      return undefined;
    }

    fetchPipelineDetails();

    if (pipelineDetails?.state?.name !== "IN_PROGRESS") {
      return undefined;
    }

    const interval = setInterval(fetchPipelineDetails, 10000);
    return () => clearInterval(interval);
  }, [
    workspace,
    repoSlug,
    pipelineUuid,
    pipelineDetails?.state?.name,
    fetchPipelineDetails,
  ]);

  const handleStepDeploy = async (stageName) => {
    // Find the step by name
    const step = pipelineSteps.find((s) => s.name === stageName);
    if (!step) {
      toast({
        title: "Error",
        description: "Step not found",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessingAction(true);

      await triggerPipelineStep(workspace, repoSlug, pipelineUuid, step.uuid);

      toast({
        title: "Success",
        description: `Triggered step: ${stageName}`,
      });

      // Refresh data after triggering
      setTimeout(() => {
        fetchPipelineDetails();
        setIsProcessingAction(false);
      }, 1000);
    } catch (err) {
      toast({
        title: "Error",
        description:
          err.response?.data?.error?.message ||
          `Failed to trigger step: ${stageName}`,
        variant: "destructive",
      });
      setIsProcessingAction(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "SUCCESSFUL":
        return (
          <Badge variant="success" className="flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" /> Successful
          </Badge>
        );
      case "FAILED":
        return (
          <Badge variant="destructive" className="flex items-center">
            <XCircle className="w-3 h-3 mr-1" /> Failed
          </Badge>
        );
      case "STOPPED":
        return (
          <Badge variant="secondary" className="flex items-center">
            <PauseCircle className="w-3 h-3 mr-1" /> Stopped
          </Badge>
        );
      case "IN_PROGRESS":
        return (
          <Badge variant="outline" className="flex items-center">
            <Clock className="w-3 h-3 mr-1 animate-spin" /> Running
          </Badge>
        );
      case "PENDING":
        return (
          <Badge variant="outline" className="flex items-center">
            <Clock className="w-3 h-3 mr-1" /> Pending
          </Badge>
        );
      case "PAUSED":
        return (
          <Badge variant="warning" className="flex items-center">
            <PauseCircle className="w-3 h-3 mr-1" /> Paused
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleBack = () => {
    navigate(`/pipelines-status/${workspace}/${repoSlug}`);
  };

  const handleRefresh = () => {
    fetchPipelineDetails();
  };

  const handleViewInBitbucket = () => {
    if (workspace && repoSlug && pipelineUuid) {
      window.open(
        `https://bitbucket.org/${workspace}/${repoSlug}/pipelines/results/${pipelineUuid}`,
        "_blank"
      );
    }
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="text-destructive text-lg font-semibold">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <div className="flex items-center mb-4 md:mb-0">
          <Breadcrumbs
            items={[
              { text: "Dashboard", href: "/dashboard" },
              {
                text: "Pipeline Status",
                href: `/pipelines-status/${workspace}/${repoSlug}`,
              },
              { text: `Build #${pipelineDetails?.build_number || ""}` },
            ]}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Button
            onClick={handleBack}
            className="flex items-center"
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={handleRefresh}
            className="flex items-center"
            variant="outline"
            size="sm"
            disabled={isLoading || isProcessingAction}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            onClick={handleViewInBitbucket}
            className="flex items-center"
            variant="outline"
            size="sm"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View in Bitbucket
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : pipelineDetails ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                  <CardTitle className="text-2xl">
                    Pipeline #{pipelineDetails.build_number}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {pipelineDetails.target?.ref_name || ""} •{" "}
                    {pipelineDetails.target?.selector?.pattern ||
                      "Default Pipeline"}
                  </CardDescription>
                </div>
                <div className="mt-2 md:mt-0">
                  {getStatusBadge(pipelineDetails.state?.name)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Created
                  </h3>
                  <p className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    {formatDateTime(pipelineDetails.created_on)}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Completed
                  </h3>
                  <p className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    {formatDateTime(pipelineDetails.completed_on) ||
                      "Running..."}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Triggered By
                  </h3>
                  <p className="flex items-center">
                    {pipelineDetails.creator?.display_name || "System"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Repository
                  </h3>
                  <p className="flex items-center">
                    {workspace}/{repoSlug}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <PipelineStages stages={pipelineSteps} onDeploy={handleStepDeploy} />
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No pipeline details found</p>
        </div>
      )}
    </div>
  );
};

export default PipelineDetailsPage;
